import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Save, Star, Loader2, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  author: "",
  rating: 5,
  quote: "",
  source: "manual",
  sort_order: 0,
  published: true,
};

const AdminReviews = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/reviews`);
      setItems(data || []);
    } catch {
      toast.error("Could not load reviews.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.author.trim() || !form.quote.trim()) {
      toast.error("Please fill in name and quote.");
      return;
    }
    try {
      await axios.post(`${API}/admin/reviews`, form);
      toast.success("Review added.");
      setForm(emptyForm);
      load();
    } catch {
      toast.error("Could not save review.");
    }
  };

  const update = async (r, patch) => {
    try {
      await axios.put(`${API}/admin/reviews/${r.id}`, {
        author: r.author,
        rating: r.rating,
        quote: r.quote,
        source: r.source,
        sort_order: r.sort_order,
        published: r.published,
        ...patch,
      });
      load();
    } catch {
      toast.error("Could not update review.");
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete review from ${r.author}?`)) return;
    try {
      await axios.delete(`${API}/admin/reviews/${r.id}`);
      toast.success("Deleted.");
      load();
    } catch {
      toast.error("Could not delete.");
    }
  };

  const [sending, setSending] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const sendReviewEmails = async () => {
    if (!window.confirm("Send the 'leave us a review' email to every guest whose stay ended 3+ days ago and hasn't been asked yet?")) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/review-emails/send-now`);
      toast.success(`Sent ${data.sent} email(s) · skipped ${data.skipped} · errors ${data.errors}`);
    } catch {
      toast.error("Could not run the sweep.");
    } finally {
      setSending(false);
    }
  };

  const seedDefaults = async () => {
    if (!window.confirm("Insert the 3 verified customer reviews (Chris · Kate O'Malley · Janet Wollen)? Safe to click even if some already exist.")) return;
    setSeeding(true);
    try {
      const { data } = await axios.post(`${API}/admin/reviews/seed-defaults`);
      toast.success(`Inserted ${data.inserted} · skipped ${data.skipped}`);
      load();
    } catch {
      toast.error("Could not seed reviews.");
    } finally {
      setSeeding(false);
    }
  };

  return (
    <section data-testid="admin-reviews">
      <p className="overline text-[#6A5F58]">
        <span className="marker-line" /> Reviews
      </p>
      <h2 className="font-serif text-3xl md:text-4xl mt-4 text-[#14100F]">
        Guest reviews on the landing page
      </h2>
      <p className="mt-3 text-[#6A5F58] text-sm max-w-2xl">
        Add up to 5 hand-picked reviews. They appear in the rose-gold carousel on the public site
        alongside any live Google reviews we auto-fetch (if the Google API key is configured).
      </p>

      {/* Post-stay email automation */}
      <div className="mt-6 rounded-2xl border border-[#DDD1BE] bg-white p-5 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1">
          <p className="font-serif text-lg text-[#14100F]">Automatic &ldquo;leave us a review&rdquo; emails</p>
          <p className="mt-1 text-sm text-[#6A5F58]">
            Runs every 6 hours in the background. Sends once per booking, 3 days after checkout, only to guests with an email on file.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={seedDefaults}
            disabled={seeding}
            data-testid="seed-default-reviews"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#0F0E0E] text-[#0F0E0E] hover:bg-[#0F0E0E] hover:text-[#F5EFE6] transition-colors text-xs uppercase tracking-[0.14em] disabled:opacity-60"
            title="Insert the 3 verified customer reviews (safe to click, won't duplicate)"
          >
            {seeding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Seed sample reviews
          </button>
          <button
            type="button"
            onClick={sendReviewEmails}
            disabled={sending}
            data-testid="send-review-emails-now"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#B76E79] transition-colors text-xs uppercase tracking-[0.14em] disabled:opacity-60"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Send sweep now
          </button>
        </div>
      </div>

      {/* Add form */}
      <form
        onSubmit={create}
        className="mt-8 bg-white border border-[#DDD1BE] rounded-2xl p-6 grid grid-cols-1 md:grid-cols-6 gap-4"
        data-testid="admin-review-form"
      >
        <div className="md:col-span-2">
          <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Guest name</Label>
          <Input
            value={form.author}
            onChange={(e) => setForm({ ...form, author: e.target.value })}
            className="mt-1 h-10 bg-[#F5EFE6]"
            data-testid="review-author"
            placeholder="e.g. Sarah T."
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Rating</Label>
          <Select value={String(form.rating)} onValueChange={(v) => setForm({ ...form, rating: Number(v) })}>
            <SelectTrigger className="mt-1 h-10 bg-[#F5EFE6]" data-testid="review-rating">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[5, 4, 3, 2, 1].map((n) => (
                <SelectItem key={n} value={String(n)}>{n} ★</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Source</Label>
          <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
            <SelectTrigger className="mt-1 h-10 bg-[#F5EFE6]" data-testid="review-source">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="google">Google</SelectItem>
              <SelectItem value="pitchup">Pitchup</SelectItem>
              <SelectItem value="manual">Verified guest</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Sort order</Label>
          <Input
            type="number"
            value={form.sort_order}
            onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })}
            className="mt-1 h-10 bg-[#F5EFE6]"
            data-testid="review-sort"
          />
        </div>
        <div className="flex items-end">
          <button type="submit" className="btn-primary text-xs font-brand tracking-[0.14em] inline-flex items-center gap-1.5 h-10" data-testid="review-add">
            <Plus size={13} /> Add
          </button>
        </div>
        <div className="md:col-span-6">
          <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Quote</Label>
          <Textarea
            value={form.quote}
            onChange={(e) => setForm({ ...form, quote: e.target.value })}
            rows={3}
            className="mt-1 bg-[#F5EFE6]"
            data-testid="review-quote"
            placeholder="Paste the review text..."
          />
        </div>
      </form>

      {/* List */}
      <div className="mt-8 space-y-3">
        {loading && (
          <div className="text-center py-6"><Loader2 size={18} className="animate-spin inline text-[#B76E79]" /></div>
        )}
        {!loading && items.length === 0 && (
          <div className="text-center py-6 text-[#6A5F58] text-sm">No reviews yet — add your first one above.</div>
        )}
        {items.map((r) => (
          <div key={r.id} className="bg-white border border-[#DDD1BE] rounded-xl p-5" data-testid={`admin-review-${r.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-[#0F0E0E]">{r.author}</span>
                  <span className="text-xs text-[#6A5F58]">· {r.source}</span>
                  <span className="inline-flex items-center gap-0.5 text-[#B76E79]">
                    {Array.from({ length: r.rating || 5 }).map((_, i) => (
                      <Star key={i} size={11} fill="currentColor" strokeWidth={0} />
                    ))}
                  </span>
                </div>
                <p className="mt-2 text-sm text-[#3A322E] italic">&ldquo;{r.quote}&rdquo;</p>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-[#6A5F58] inline-flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={r.published}
                    onChange={(e) => update(r, { published: e.target.checked })}
                    className="accent-[#B76E79]"
                  />
                  Published
                </label>
                <button
                  onClick={() => remove(r)}
                  className="p-2 rounded-full text-[#8C2E39] hover:bg-[#8C2E39]/10"
                  aria-label="Delete review"
                  data-testid={`review-delete-${r.id}`}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default AdminReviews;
